import { useBookingRecord } from "@/app/Bookings/Hooks/useBookingRecord";
import { Button } from "@/components/catalyst/button";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import {
  AlertTriangleIcon,
  CloudUploadIcon,
  CreditCardIcon,
  DownloadIcon,
  PencilIcon,
  PlusIcon,
  ReceiptIcon,
  Trash2Icon,
} from "lucide-react";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { useRefundActions } from "@/app/Refunds/Hooks/useRefundActions";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  refundsQuery,
  updateRefundMutation,
  deleteRefundPaymentMutation,
  deleteReceiptMutation,
  RefundRecord,
} from "@/app/Refunds/GraphQL/refundMutations";
import { EditReceiptDialog } from "@/app/Refunds/Components/EditReceiptDialog";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { UploadReceiptDialog } from "@/app/Refunds/Components/UploadReceiptDialog";
import { AddRefundPaymentDialog } from "@/app/Refunds/Components/AddRefundPaymentDialog";
import { CreateRefundDialog } from "@/app/Refunds/Components/CreateRefundDialog";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import { useTranslations } from "use-intl";
import {
  Currency,
  ModelType,
  PaymentStatus,
  RefundPaymentMethod,
  RefundStatus,
  RefundType,
  UpdateRefundInput,
} from "@/gql/graphql";
import { Badge } from "@/components/catalyst/badge";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { formatDateTime } from "@/lib/Formatters/formatDateTime";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";

function getPaymentMethodLabel(method: RefundPaymentMethod): string {
  switch (method) {
    case RefundPaymentMethod.DirectDeposit:
      return "Direct Deposit";
    case RefundPaymentMethod.Paypal:
      return "PayPal";
    default:
      return "Unknown";
  }
}

function getPaymentMethodBadgeColor(
  method: RefundPaymentMethod,
): "blue" | "purple" {
  switch (method) {
    case RefundPaymentMethod.DirectDeposit:
      return "blue";
    case RefundPaymentMethod.Paypal:
      return "purple";
    default:
      return "blue";
  }
}

function isReceiptDateOutsideBooking(
  receiptDate: string | null,
  departAt: string,
  deliverAt: string,
): boolean {
  if (!receiptDate) {
    return false;
  }
  const receipt = new Date(receiptDate);
  const depart = new Date(departAt);
  const deliver = new Date(deliverAt);

  depart.setHours(0, 0, 0, 0);
  deliver.setHours(23, 59, 59, 999);

  return receipt < depart || receipt > deliver;
}

function getRefundStatusBadgeColor(
  status: RefundStatus,
): "zinc" | "amber" | "green" | "red" {
  switch (status) {
    case RefundStatus.Pending:
      return "zinc";
    case RefundStatus.Submitted:
      return "amber";
    case RefundStatus.Completed:
      return "green";
    case RefundStatus.Rejected:
      return "red";
    default:
      return "zinc";
  }
}

function getRefundStatusLabel(status: RefundStatus): string {
  switch (status) {
    case RefundStatus.Pending:
      return "Pending";
    case RefundStatus.Submitted:
      return "Submitted";
    case RefundStatus.Completed:
      return "Completed";
    case RefundStatus.Rejected:
      return "Rejected";
    default:
      return "Unknown";
  }
}

function getRefundTypeLabel(type: RefundType): string {
  switch (type) {
    case RefundType.Fuel:
      return "Fuel";
    case RefundType.Ferry:
      return "Ferry";
  }
}

export function BookingReceiptsScreen() {
  const booking = useBookingRecord();
  const { open: openCreateRefundDialog } = useDialog(CreateRefundDialog);

  const { data: refundData } = useSuspenseGqlQuery(refundsQuery, {
    object_type: ModelType.Booking,
    object_id: booking.id,
  });

  const refunds = refundData?.refunds ?? [];
  const existingTypes = new Set(refunds.map((r) => r.type));
  const missingTypes = [RefundType.Fuel, RefundType.Ferry].filter(
    (t) => !existingTypes.has(t),
  );

  return (
    <div className="space-y-12 py-6">
      {refunds.map((refund) => (
        <RefundSection key={refund.id} refund={refund} booking={booking} />
      ))}

      {missingTypes.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {missingTypes.map((type) => (
            <Button
              key={type}
              outline
              LeadingIcon={PlusIcon}
              onClick={() => {
                openCreateRefundDialog({
                  bookingId: booking.id,
                  refundType: type,
                  defaultCurrency: booking.currency,
                });
              }}
            >
              Create {getRefundTypeLabel(type)} Refund
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RefundSection({
  refund,
  booking,
}: {
  refund: RefundRecord;
  booking: BookingRecord;
}) {
  const { mutateAsync: updateRefund } = useGqlMutation(updateRefundMutation);
  const typeLabel = getRefundTypeLabel(refund.type);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-zinc-700 dark:bg-zinc-900">
      <RecordScreenFormCard<UpdateRefundInput>
        onSubmit={async (values) => {
          await updateRefund({
            id: refund.id,
            input: values,
          });
        }}
        initialValues={{
          currency: refund.currency,
          maximum_refund_amount: refund.maximum_refund_amount,
          bank_country: refund.bank_country ?? null,
          bank_name: refund.bank_name ?? null,
          bank_account_name: refund.bank_account_name ?? null,
          bank_account_number: refund.bank_account_number ?? null,
          bank_identifier_code: refund.bank_identifier_code ?? null,
          recipient_address: refund.recipient_address ?? null,
        }}
        title={`${typeLabel} Refund`}
        subtitle={
          <span className="flex items-center gap-3">
            <Badge color={getRefundStatusBadgeColor(refund.status)}>
              {getRefundStatusLabel(refund.status)}
            </Badge>
          </span>
        }
        view={<RefundView refund={refund} booking={booking} />}
        actions={<RefundActionsMenu refund={refund} />}
      >
        <RefundFormFields />
      </RecordScreenFormCard>
    </div>
  );
}

function RefundFormFields() {
  const { values } = useFormState<UpdateRefundInput>();

  return (
    <>
      <FormSectionHeader title="Refund Configuration" />
      <CurrencyTypeInput name="currency" label="Currency" />
      <CurrencyInput
        name="maximum_refund_amount"
        label="Maximum Refund Amount"
        currency={values.currency ?? Currency.Aud}
      />

      <FormSectionHeader title="Bank Details" />
      <TextInput name="bank_country" label="Country" />
      <TextInput name="bank_name" label="Bank Name" />
      <TextInput name="bank_account_name" label="Bank Account Name" />
      <TextInput name="bank_account_number" label="Bank Account Number" />
      <TextInput
        name="bank_identifier_code"
        label="BSB / Sort Code / Routing number"
      />

      <FormSectionHeader title="Recipient Address" />
      <TextAreaInput name="recipient_address" label="Recipient Address" />
    </>
  );
}

function RefundView({
  refund,
  booking,
}: {
  refund: RefundRecord;
  booking: BookingRecord;
}) {
  const receiptsTotal = refund.receipts.reduce(
    (sum, r) =>
      sum +
      (r.claimable_converted_amount_inc_tax ?? r.claimable_amount_inc_tax ?? 0),
    0,
  );
  const refundAmount =
    refund.maximum_refund_amount === null
      ? receiptsTotal
      : Math.min(receiptsTotal, refund.maximum_refund_amount);
  const totalPayments = refund.payments.reduce((sum, p) => sum + p.amount, 0);
  const remainingAmount = refundAmount - totalPayments;

  return (
    <div className="space-y-6 pt-4">
      <InvoiceHeader refund={refund} />

      <RefundInvoice
        refund={refund}
        booking={booking}
        receiptsTotal={receiptsTotal}
        totalPayments={totalPayments}
        remainingAmount={remainingAmount}
      />

      <SupplierPaymentSection refund={refund} />
    </div>
  );
}

function getPaymentStatusBadgeColor(
  status: PaymentStatus,
): "zinc" | "amber" | "green" | "red" | "blue" {
  switch (status) {
    case PaymentStatus.Paid:
      return "green";
    case PaymentStatus.Pending:
      return "amber";
    case PaymentStatus.Unpaid:
      return "zinc";
    case PaymentStatus.Voided:
      return "red";
    case PaymentStatus.Refunded:
    case PaymentStatus.PartiallyRefunded:
      return "blue";
    default:
      return "zinc";
  }
}

function SupplierPaymentSection({ refund }: { refund: RefundRecord }) {
  const payments = refund.supplierPayments;
  const t = useTranslations("common.tax_type");

  return (
    <div>
      <div className="flex items-center justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Supplier Charges
        </h4>
      </div>

      {payments.length === 0 ? (
        <p className="py-3 text-sm text-zinc-500">
          A supplier charge will be created when the refund is completed.
        </p>
      ) : (
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="py-2 font-medium">Created</th>
              <th className="py-2 font-medium">Description</th>
              <th className="py-2 font-medium">Tax</th>
              <th className="py-2 font-medium">Status</th>
              <th className="py-2 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {payments.map((payment) => (
              <tr key={payment.id}>
                <td className="py-3 text-zinc-700 dark:text-zinc-300">
                  {formatDate(payment.created_at)}
                </td>
                <td className="py-3 text-zinc-700 dark:text-zinc-300">
                  {payment.description || (
                    <span className="text-zinc-400">—</span>
                  )}
                </td>
                <td className="py-3 text-zinc-700 dark:text-zinc-300">
                  {t(payment.tax_type)}
                </td>
                <td className="py-3">
                  <Badge color={getPaymentStatusBadgeColor(payment.status)}>
                    {payment.status}
                  </Badge>
                </td>
                <td className="py-3 text-right font-medium tabular-nums">
                  {formatCurrency(payment.amount, payment.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RefundActionsMenu({ refund }: { refund: RefundRecord }) {
  const getActions = useRefundActions();
  return <IconMenuActions actions={getActions(refund)} />;
}

function InvoiceHeader({ refund }: { refund: RefundRecord }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Refund To
        </h4>
        <dl className="mt-2 space-y-1 text-sm">
          <InvoiceField label="Country" value={refund.bank_country} />
          <InvoiceField label="Bank" value={refund.bank_name} />
          <InvoiceField label="Account Name" value={refund.bank_account_name} />
          <InvoiceField
            label="Account Number"
            value={refund.bank_account_number}
          />
          <InvoiceField
            label="BSB / Sort / Routing"
            value={refund.bank_identifier_code}
          />
          <InvoiceField label="Address" value={refund.recipient_address} />
        </dl>
      </div>
      <div className="sm:text-right">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Refund Details
        </h4>
        <dl className="mt-2 space-y-1 text-sm">
          <InvoiceField label="Currency" value={refund.currency} alignEnd />
          <InvoiceField
            label="Maximum"
            value={formatCurrency(
              refund.maximum_refund_amount,
              refund.currency,
            )}
            alignEnd
          />
        </dl>
      </div>
    </div>
  );
}

function InvoiceField({
  label,
  value,
  alignEnd,
}: {
  label: string;
  value: string | null | undefined;
  alignEnd?: boolean;
}) {
  return (
    <div
      className={`flex gap-2 ${alignEnd ? "justify-end" : "justify-between sm:justify-start"}`}
    >
      <dt className="text-zinc-500">{label}:</dt>
      <dd className="text-zinc-900 dark:text-zinc-100">
        {value ? value : <span className="text-zinc-400">—</span>}
      </dd>
    </div>
  );
}

function RefundInvoice({
  refund,
  booking,
  receiptsTotal,
  totalPayments,
  remainingAmount,
}: {
  refund: RefundRecord;
  booking: BookingRecord;
  receiptsTotal: number;
  totalPayments: number;
  remainingAmount: number;
}) {
  const { open: openUploadDialog } = useDialog(UploadReceiptDialog);
  const { open: openEditReceiptDialog } = useDialog(EditReceiptDialog);
  const { open: openAddPaymentDialog } = useDialog(AddRefundPaymentDialog);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { mutateAsync: deleteReceipt } = useGqlMutation(deleteReceiptMutation);
  const { mutateAsync: deletePayment } = useGqlMutation(
    deleteRefundPaymentMutation,
  );

  const receipts = refund.receipts;
  const payments = refund.payments;
  const currency = refund.currency;
  const remainingClass =
    remainingAmount > 0
      ? "text-amber-600"
      : remainingAmount < 0
        ? "text-red-600"
        : "text-green-600";

  return (
    <div>
      <div className="flex items-center justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Receipts
        </h4>
        <Button
          plain
          onClick={() => {
            openUploadDialog({
              objectId: refund.id,
              objectType: ModelType.Refund,
            });
          }}
          LeadingIcon={CloudUploadIcon}
        >
          Upload
        </Button>
      </div>

      <table className="mt-2 w-full text-sm">
        {receipts.length > 0 ? (
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="py-2 font-medium">Date</th>
              <th className="py-2 font-medium">Type</th>
              <th className="py-2 font-medium">Description</th>
              <th className="py-2 text-right font-medium">Amount</th>
              <th className="py-2"></th>
            </tr>
          </thead>
        ) : null}
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {receipts.length === 0 ? (
            <tr>
              <td colSpan={5} className="py-6">
                <EmptyState
                  Icon={ReceiptIcon}
                  header="No receipts uploaded"
                  subheader="Upload receipts for this booking"
                />
              </td>
            </tr>
          ) : (
            receipts.map((receipt) => {
              const receiptCurrency = receipt.currency ?? Currency.Aud;
              const hasDateWarning = receipt.date
                ? isReceiptDateOutsideBooking(
                    receipt.date,
                    booking.depart_at,
                    booking.deliver_at,
                  )
                : false;
              const hasCurrencyMismatch = receiptCurrency !== booking.currency;
              const displayAmount =
                receipt.claimable_converted_amount_inc_tax ??
                receipt.claimable_amount_inc_tax ??
                0;
              const displayCurrency =
                receipt.converted_currency ?? receiptCurrency;

              return (
                <tr key={receipt.id} className="align-middle">
                  <td className="py-3 text-zinc-700 dark:text-zinc-300">
                    {receipt.date ? (
                      <span className="inline-flex items-center gap-1.5">
                        {formatDate(receipt.date)}
                        {hasDateWarning ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertTriangleIcon className="size-3.5 text-amber-500" />
                            </TooltipTrigger>
                            <TooltipContent>
                              Receipt date is outside the booking dates
                            </TooltipContent>
                          </Tooltip>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="py-3 text-zinc-700 dark:text-zinc-300">
                    {receipt.type}
                  </td>
                  <td className="py-3 text-zinc-700 dark:text-zinc-300">
                    {receipt.description || (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="py-3 text-right font-medium tabular-nums">
                    <div
                      title={
                        hasCurrencyMismatch
                          ? `Converted from ${receiptCurrency}`
                          : undefined
                      }
                    >
                      {formatCurrency(displayAmount, displayCurrency)}
                    </div>
                    {hasCurrencyMismatch ? (
                      <div className="text-xs font-normal text-zinc-500">
                        {formatCurrency(
                          receipt.claimable_amount_inc_tax ?? 0,
                          receiptCurrency,
                        )}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        plain
                        onClick={() =>
                          openEditReceiptDialog({ receiptId: receipt.id })
                        }
                      >
                        <PencilIcon data-slot="icon" />
                      </Button>
                      {receipt.image ? (
                        <button
                          type="button"
                          onClick={async () => {
                            const response = await fetch(receipt.image!.url);
                            const blob = await response.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `receipt-${receipt.id}.${blob.type.split("/")[1] || "jpg"}`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          className="rounded p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                        >
                          <DownloadIcon className="size-4" />
                        </button>
                      ) : null}
                      <Button
                        plain
                        onClick={() => {
                          openConfirmDelete({
                            title: "Delete receipt?",
                            message:
                              "This receipt will be permanently removed from the refund.",
                            onDelete: () => deleteReceipt({ id: receipt.id }),
                          });
                        }}
                      >
                        <Trash2Icon data-slot="icon" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
        <tfoot>
          <tr className="border-t border-zinc-200 dark:border-zinc-700">
            <th
              scope="row"
              colSpan={3}
              className="py-3 text-right text-xs font-normal text-zinc-500"
            >
              Subtotal
            </th>
            <td className="py-3 text-right font-medium tabular-nums">
              {formatCurrency(receiptsTotal, currency)}
            </td>
            <td></td>
          </tr>

          <tr>
            <td colSpan={5} className="pt-6">
              <div className="flex items-center justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Payments
                </h4>
                <Button
                  plain
                  onClick={() => {
                    openAddPaymentDialog({
                      refundId: refund.id,
                      currency: refund.currency,
                    });
                  }}
                  LeadingIcon={PlusIcon}
                >
                  Add Payment
                </Button>
              </div>
            </td>
          </tr>

          {payments.length === 0 ? (
            <tr>
              <td colSpan={5} className="py-6">
                <EmptyState
                  Icon={CreditCardIcon}
                  header="No payments recorded"
                  subheader="Record payments made to the customer"
                />
              </td>
            </tr>
          ) : (
            payments.map((payment) => (
              <tr key={payment.id} className="align-middle">
                <td className="py-3 text-zinc-700 dark:text-zinc-300">
                  {payment.refunded_at ? (
                    formatDateTime(payment.refunded_at)
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </td>
                <td className="py-3">
                  <Badge color={getPaymentMethodBadgeColor(payment.method)}>
                    {getPaymentMethodLabel(payment.method)}
                  </Badge>
                </td>
                <td className="py-3 text-zinc-700 dark:text-zinc-300">
                  {payment.transaction_number || (
                    <span className="text-zinc-400">—</span>
                  )}
                </td>
                <td className="py-3 text-right font-medium tabular-nums">
                  {formatCurrency(payment.amount, payment.currency)}
                </td>
                <td className="py-3 text-right">
                  <Button
                    plain
                    onClick={() => {
                      openConfirmDelete({
                        title: "Delete payment?",
                        message:
                          "This payment record will be permanently removed. The supplier payment line will remain.",
                        onDelete: () => deletePayment({ id: payment.id }),
                      });
                    }}
                  >
                    <Trash2Icon data-slot="icon" />
                  </Button>
                </td>
              </tr>
            ))
          )}

          <tr className="border-t border-zinc-200 dark:border-zinc-700">
            <th
              scope="row"
              colSpan={3}
              className="py-3 text-right text-xs font-normal text-zinc-500"
            >
              Total Paid
            </th>
            <td className="py-3 text-right font-medium tabular-nums">
              {formatCurrency(totalPayments, currency)}
            </td>
            <td></td>
          </tr>

          <tr className="border-t border-zinc-200 dark:border-zinc-700">
            <th
              scope="row"
              colSpan={3}
              className="py-3 text-right text-sm font-semibold text-zinc-900 dark:text-zinc-100"
            >
              Remaining
            </th>
            <td
              className={`py-3 text-right text-sm font-semibold tabular-nums ${remainingClass}`}
            >
              {formatCurrency(remainingAmount, currency, { showZero: true })}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function FormSectionHeader({ title }: { title: string }) {
  return (
    <div className="col-span-full mt-2 border-b border-zinc-200 pb-2 dark:border-zinc-700">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        {title}
      </h3>
    </div>
  );
}
